import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { faSparkles as faSparklesSolid } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';

import { Button } from 'ui-components/components/button';
import { emptyArray } from 'ui-components/default-props/empty';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { Features } from '../../../../../constants/features';
import { SellerSettings } from '../../../../../enums/seller-settings/seller-settings';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { useSellerSetting } from '../../../../../hooks/use-seller-setting';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import Global from '../../../../../store/global-selectors';
import { usePackingCalculation } from '../../../../../packing/hooks/use-packing-calculation';
import type { JobResultResponse } from '../../../../../packing/services/packing-service';
import { validateAndTransform, DEFAULT_OPTIMIZATION_FACTOR } from '../../../../../packing/utils/data-transformer';

import { PackingResultCard } from './components/packing-result-card/packing-result-card';
import { useOrderData } from './hooks/use-order-data';
import { useApplyRecommendation } from './hooks/use-apply-recommendation';
import { messages } from './messages';
import style from './packing-recommend.module.less';
import {
  cacheResult,
  clearCachedResult,
  clearCalculating,
  clearError,
  getActiveJob,
  getCachedResult,
  hasError,
  isCurrentlyCalculating,
  markError,
  setActiveJob,
  triggerCalculation,
  useOnTriggerCalculation,
} from './use-packing-applied';

const PERSISTED_ERROR_PLACEHOLDER = { code: 'CALCULATION_FAILED', message: '', details: {}, retryable: true };

type PackingRecommendProps = {
  fulfillmentPlanId: string;
  hideButton?: boolean;
};

export const PackingRecommend = ({ fulfillmentPlanId, hideButton = false }: PackingRecommendProps) => {
  const intl = useIntl();
  const isPackingRecommendationEnabled = useIsSellerFeatureEnabled(Features.PackingRecommendations);
  const currentOptimizationFactor = useSellerSetting(SellerSettings.packingRecommendationOptimizationFactor) ?? DEFAULT_OPTIMIZATION_FACTOR;
  const context = useFulfillmentPlannerContext();
  const [showResults, setShowResults] = useState(() => getCachedResult(fulfillmentPlanId) !== null || hasError(fulfillmentPlanId));
  const [isAppliedDismissed, setIsAppliedDismissed] = useState(false);

  const { order, availablePackages, sellerDimensionUnit, sellerWeightUnit } = useOrderData(fulfillmentPlanId);

  const handleCalculationComplete = useCallback(
    (completedResult: JobResultResponse) => {
      clearCalculating(fulfillmentPlanId);
      cacheResult(fulfillmentPlanId, completedResult);
      setShowResults(true);
    },
    [fulfillmentPlanId],
  );

  const handleCalculationError = useCallback(() => {
    clearCalculating(fulfillmentPlanId);
    markError(fulfillmentPlanId);
    setShowResults(true);
  }, [fulfillmentPlanId]);

  const {
    isCalculating,
    jobId,
    result: hookResult,
    error,
    startCalculation,
    resumePolling,
    reset,
  } = usePackingCalculation({
    onComplete: handleCalculationComplete,
    onError: handleCalculationError,
  });

  const result = hookResult ?? getCachedResult(fulfillmentPlanId);
  const persistedError = !error && hasError(fulfillmentPlanId);
  const effectiveError = error ?? (persistedError ? PERSISTED_ERROR_PLACEHOLDER : null);

  const { handleApply, isApplied } = useApplyRecommendation(
    result,
    availablePackages,
    fulfillmentPlanId,
    sellerDimensionUnit,
    sellerWeightUnit,
  );

  const prevIsAppliedRef = useRef(false);
  const isRerunningRef = useRef(false);
  useEffect(() => {
    if (prevIsAppliedRef.current && !isApplied) {
      setIsAppliedDismissed(false);
      if (isRerunningRef.current) {
        isRerunningRef.current = false;
      } else {
        clearCachedResult(fulfillmentPlanId);
        setShowResults(false);
        reset();
      }
    }
    prevIsAppliedRef.current = isApplied;
  }, [isApplied, reset, fulfillmentPlanId]);

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const packages = fulfillmentPlan?.packages ?? emptyArray;
  const packagesSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify(
      packages.map((p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number> }) => ({
        id: p.packageId,
        w: p.weight?.value,
        d: p.dimensions,
      })),
    );
    if (packagesSignatureRef.current === null) {
      packagesSignatureRef.current = signature;
      return;
    }
    if (signature !== packagesSignatureRef.current && showResults && !isApplied && !isCalculating) {
      clearCachedResult(fulfillmentPlanId);
      setShowResults(false);
      reset();
    }
    packagesSignatureRef.current = signature;
  }, [packages, showResults, isApplied, isCalculating, fulfillmentPlanId, reset]);

  const isCalculatingRef = useRef(isCalculating);
  isCalculatingRef.current = isCalculating;

  const handleCalculate = useCallback(
    (isRerun = false) => {
      if (isCalculatingRef.current) return;
      if (isRerun) {
        isRerunningRef.current = true;
      }

      const { request } = validateAndTransform(order, availablePackages, {
        sourceDimensionUnit: sellerDimensionUnit,
        sourceWeightUnit: sellerWeightUnit,
        optimizationFactor: currentOptimizationFactor,
      });

      if (!request) {
        NotificationService.showNotification({
          content: intl.formatMessage(messages.validationFailed),
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
        clearCalculating(fulfillmentPlanId);
        return;
      }

      startCalculation(request);
    },
    [order, availablePackages, sellerDimensionUnit, sellerWeightUnit, currentOptimizationFactor, startCalculation, intl, fulfillmentPlanId],
  );

  useOnTriggerCalculation(fulfillmentPlanId, handleCalculate);

  useEffect(() => {
    if (jobId && isCalculating) {
      const existing = getActiveJob(fulfillmentPlanId);
      const startTime = existing?.startTime ?? Date.now();
      setActiveJob(fulfillmentPlanId, jobId, startTime);
    }
  }, [fulfillmentPlanId, jobId, isCalculating]);

  const resumePollingRef = useRef(resumePolling);
  resumePollingRef.current = resumePolling;
  useEffect(() => {
    if (isCurrentlyCalculating(fulfillmentPlanId)) {
      const activeJob = getActiveJob(fulfillmentPlanId);
      if (activeJob) {
        resumePollingRef.current(activeJob.jobId, activeJob.startTime);
      }
    }
  }, [fulfillmentPlanId]);

  const handleCalculateClick = useCallback(() => {
    handleCalculate(false);
  }, [handleCalculate]);

  const handleCancel = useCallback(() => {
    clearCalculating(fulfillmentPlanId);
    clearCachedResult(fulfillmentPlanId);
    clearError(fulfillmentPlanId);
    setShowResults(false);
    reset();
  }, [fulfillmentPlanId, reset]);

  const handleDismissApplied = useCallback(() => {
    setIsAppliedDismissed(true);
  }, []);

  const handleRerun = useCallback(() => {
    setIsAppliedDismissed(false);
    triggerCalculation(fulfillmentPlanId, true);
  }, [fulfillmentPlanId]);

  if (!isPackingRecommendationEnabled) {
    return null;
  }

  const { onPackageChange } = context ?? {};
  const hasContextCallbacks = onPackageChange && typeof onPackageChange === 'function';
  const canShow = hasContextCallbacks && order.items?.length > 0 && availablePackages.length > 0;

  if (!canShow) {
    return null;
  }

  if (isApplied) {
    if (isAppliedDismissed) {
      return null;
    }
    return (
      <FlexRow className={style.appliedContainer} gap={Gap.Space8}>
        <FontAwesomeIcon className={style.appliedSparkleIcon} icon={faSparklesSolid} />
        <Text className={style.appliedTitle} textType={TextType.Label} value={<FormattedMessage {...messages.recommendationApplied} />} />
        <Button
          ariaLabel={intl.formatMessage(messages.rerun)}
          buttonSize='small'
          className={style.rerunButton}
          onClick={handleRerun}
          theme={THEME_CONSTANTS.white}
        >
          <FontAwesomeIcon className={style.rerunIcon} icon={faArrowRotateRight} />
          <FormattedMessage {...messages.rerun} />
        </Button>
        <Button
          ariaLabel={intl.formatMessage(messages.cancel)}
          className={style.closeButton}
          onClick={handleDismissApplied}
          theme={THEME_CONSTANTS.unstyled}
        >
          <FontAwesomeIcon className={style.closeIcon} icon={faTimes} />
        </Button>
      </FlexRow>
    );
  }

  if (!isCalculating && !showResults) {
    if (hideButton) {
      return null;
    }
    return (
      <FlexRow className={style.buttonRow}>
        <Button ariaLabel={intl.formatMessage(messages.recommendPackage)} className={style.recommendButton} onClick={handleCalculateClick}>
          <FontAwesomeIcon className={style.sparkleIcon} icon={faSparkles} />
          <FormattedMessage {...messages.recommendPackage} />
        </Button>
      </FlexRow>
    );
  }

  return (
    <PackingResultCard
      error={effectiveError}
      isCalculating={isCalculating}
      onApply={handleApply}
      onCancel={handleCancel}
      onRerun={handleRerun}
      result={result}
      sellerDimensionUnit={sellerDimensionUnit}
    />
  );
};
